// Shared Visualization Engine Component
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { CPConfig, SequenceConfig, AssetCoordinate, ConveyorBelt } from '../../types/config';
import { passengerFlowAnimationService, FlowAnimationData } from '../../services/passengerFlowAnimationService';

interface VisualizationEngineProps {
  checkpointConfig: CPConfig;
  sequenceConfig: SequenceConfig;
  occupancyData: Record<string, number> | null;
  flowData?: Record<string, number> | null; // Add flowOut data
  thresholds?: Record<string, { green: number; yellow: number; amber: number; minflow: number; maxflow: number }>;
  checkpointId: string;
  className?: string;
}

export const VisualizationEngine: React.FC<VisualizationEngineProps> = ({
  checkpointConfig,
  sequenceConfig,
  occupancyData,
  flowData,
  thresholds,
  checkpointId,
  className = ""
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ctx, setCtx] = useState<CanvasRenderingContext2D | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [animationData, setAnimationData] = useState<Map<string, FlowAnimationData>>(new Map());
  const [hoveredZone, setHoveredZone] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  // Canvas font constants matching application theme
  // Using the exact same font stack as defined in index.css
  const CANVAS_FONT_FAMILY = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif";
  const GRID_FONT = `10px ${CANVAS_FONT_FAMILY}`;
  const EXIT_FONT = `bold 10px ${CANVAS_FONT_FAMILY}`;
  
  // Tag label colors matching application theme colors from index.css
  const TAG_LABEL_COLORS = {
    text: '#ffffff',        // White text for contrast
    stroke: '#111827',      // --darkest-bg from CSS variables
    strokeWidth: 3,         // Standard outline width
    altText: '#3182ce',     // --primary-blue from CSS variables
    altStroke: '#1a202c'    // --dark-bg from CSS variables
  };

  // Default thresholds if not provided
  const defaultThresholds = { green: 20, yellow: 25, amber: 30 };

  // Calculate rendering parameters
  const gridConfig = checkpointConfig.grids;
  const assets = checkpointConfig.assets;
  
  const scale = Math.min(
    (canvasSize.width - 100) / gridConfig.x,
    (canvasSize.height - 100) / gridConfig.y
  );
  const offsetX = (canvasSize.width - gridConfig.x * scale) / 2;
  const offsetY = (canvasSize.height - gridConfig.y * scale) / 2;

  // Helper functions for heatmap and dots
  const getColorFromOccupancy = (occupancy: number, thresholds: {green: number, yellow: number, amber: number}) => {
    if (occupancy==0) return 'rgba(107, 114, 128, 0.2)'; // Grey for zero occupancy
    if (occupancy <= thresholds.green) return 'rgba(34, 197, 94, 0.35)';
    if (occupancy <= thresholds.yellow) return 'rgba(234, 179, 8, 0.35)';
    if (occupancy <= thresholds.amber) return 'rgba(245, 101, 101, 0.35)';
    return 'rgba(239, 68, 68, 0.4)';
  };
  // Initialize canvas
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      
      if (context) {
        setCtx(context);

        const resizeCanvas = () => {
          const container = canvas.parentElement;
          if (container) {
            const rect = container.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              canvas.width = rect.width;
              canvas.height = rect.height;
              setCanvasSize({ width: rect.width, height: rect.height });
            }
          }
        };

        resizeCanvas();
        const timeouts = [50, 100, 250, 500];
        timeouts.forEach(delay => setTimeout(resizeCanvas, delay));
        
        // Fixed ResizeObserver with debouncing to prevent loop errors
        if (window.ResizeObserver && canvas.parentElement) {
          let resizeTimeout: NodeJS.Timeout;
          const resizeObserver = new ResizeObserver((entries) => {
            // Clear any pending resize
            if (resizeTimeout) {
              clearTimeout(resizeTimeout);
            }
            // Debounce the resize to prevent loop errors
            resizeTimeout = setTimeout(() => {
              window.requestAnimationFrame(() => {
                resizeCanvas();
              });
            }, 10);
          });
          resizeObserver.observe(canvas.parentElement);
          return () => {
            resizeObserver.disconnect();
            if (resizeTimeout) {
              clearTimeout(resizeTimeout);
            }
          };
        }
        
        window.addEventListener('resize', resizeCanvas);
        return () => window.removeEventListener('resize', resizeCanvas);
      }
    }
  }, []);

  // Grid to canvas coordinate conversion
  const gridToCanvas = useCallback((gridX: number, gridY: number) => {
    return {
      x: offsetX + gridX * scale,
      y: offsetY + (gridConfig.y - gridY) * scale
    };
  }, [offsetX, offsetY, scale, gridConfig.y]);

  // Canvas to grid coordinate conversion (for mouse hover)
  const canvasToGrid = useCallback((canvasX: number, canvasY: number) => {
    return {
      x: (canvasX - offsetX) / scale,
      y: gridConfig.y - (canvasY - offsetY) / scale
    };
  }, [offsetX, offsetY, scale, gridConfig.y]);

  // Check if a point is inside a polygon
  const isPointInPolygon = useCallback((point: { x: number; y: number }, polygon: Array<{ x: number; y: number }>) => {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i].x, yi = polygon[i].y;
      const xj = polygon[j].x, yj = polygon[j].y;
      
      const intersect = ((yi > point.y) !== (yj > point.y))
        && (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
      if (intersect) inside = !inside;
    }
    return inside;
  }, []);

  // Helper function to render tag labels along a line
  const renderTagLabel = useCallback((
    ctx: CanvasRenderingContext2D,
    tagLabel: {
      text: string;
      start: { x: number; y: number };
      end: { x: number; y: number };
      style?: {
        fontSize?: number;
        color?: string;
        bold?: boolean;
        strokeColor?: string;
      };
    },
    scale: number,
    gridToCanvas: (x: number, y: number) => { x: number; y: number }
  ) => {
    const { text, start, end, style = {} } = tagLabel;
    
    // Convert grid coordinates to canvas coordinates
    const startCanvas = gridToCanvas(start.x, start.y);
    const endCanvas = gridToCanvas(end.x, end.y);
    
    // Calculate angle and distance
    const dx = endCanvas.x - startCanvas.x;
    const dy = endCanvas.y - startCanvas.y;
    const angle = Math.atan2(dy, dx);
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    // Calculate center point of the line
    const centerX = (startCanvas.x + endCanvas.x) / 2;
    const centerY = (startCanvas.y + endCanvas.y) / 2;
    
    ctx.save();
    
    // Move to center and rotate
    ctx.translate(centerX, centerY);
    ctx.rotate(angle);
    
    // Flip text if upside down (angle > 90° or < -90°)
    if (angle > Math.PI/2 || angle < -Math.PI/2) {
      ctx.rotate(Math.PI);
    }
    
    // Set text styles matching application theme
    const fontSize = style.fontSize || Math.max(12, scale * 0.8);
    ctx.font = `${style.bold ? 'bold ' : ''}${fontSize}px ${CANVAS_FONT_FAMILY}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Check if text fits, if not, scale it down
    const textWidth = ctx.measureText(text).width;
    if (textWidth > distance * 0.9) { // 90% to leave margin
      const scaleRatio = (distance * 0.9) / textWidth;
      ctx.scale(scaleRatio, scaleRatio);
    }
    
    // Draw text with outline for visibility - using theme colors
    ctx.strokeStyle = style.strokeColor || TAG_LABEL_COLORS.stroke;
    ctx.lineWidth = TAG_LABEL_COLORS.strokeWidth;
    ctx.strokeText(text, 0, 0);
    
    // Fill text with theme color
    ctx.fillStyle = style.color || TAG_LABEL_COLORS.text;
    ctx.fillText(text, 0, 0);
    
    ctx.restore();
  }, [CANVAS_FONT_FAMILY, TAG_LABEL_COLORS]);

  // Handle mouse move for hover detection
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const canvasX = event.clientX - rect.left;
    const canvasY = event.clientY - rect.top;
    const gridPoint = canvasToGrid(canvasX, canvasY);
    
    // Store position relative to the canvas container
    setMousePosition({ x: canvasX, y: canvasY });
    
    // Check which zone the mouse is hovering over
    let foundZone: string | null = null;
    if (checkpointConfig.heatmap) {
      for (const [assetCode, heatmapData] of Object.entries(checkpointConfig.heatmap)) {
        if (heatmapData.polygon && isPointInPolygon(gridPoint, heatmapData.polygon)) {
          foundZone = assetCode;
          break;
        }
      }
    }
    
    setHoveredZone(foundZone);
    
    // Update canvas cursor style
    if (canvasRef.current) {
      canvasRef.current.style.cursor = foundZone ? 'pointer' : 'crosshair';
    }
  }, [canvasToGrid, checkpointConfig.heatmap, isPointInPolygon]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    setHoveredZone(null);
  }, []);
  // Rendering functions
  const renderBaseGrid = useCallback(() => {
    if (!ctx) return;

    ctx.strokeStyle = '#374151';
    ctx.lineWidth = 1;

    // Vertical grid lines
    for (let x = 0; x <= gridConfig.x; x++) {
      const canvasX = offsetX + x * scale;
      ctx.beginPath();
      ctx.moveTo(canvasX, offsetY);
      ctx.lineTo(canvasX, offsetY + gridConfig.y * scale);
      ctx.stroke();
    }

    // Horizontal grid lines
    for (let y = 0; y <= gridConfig.y; y++) {
      const canvasY = offsetY + (gridConfig.y - y) * scale;
      ctx.beginPath();
      ctx.moveTo(offsetX, canvasY);
      ctx.lineTo(offsetX + gridConfig.x * scale, canvasY);
      ctx.stroke();
    }

    // Grid numbers
    ctx.fillStyle = '#6b7280';
    ctx.font = GRID_FONT;
    ctx.textAlign = 'center';

    for (let x = 0; x <= gridConfig.x; x += 2) {
      const canvasX = offsetX + x * scale;
      ctx.fillText(x.toString(), canvasX, offsetY + gridConfig.y * scale + 15);
    }

    ctx.textAlign = 'right';
    for (let y = 0; y <= gridConfig.y; y += 2) {
      const canvasY = offsetY + (gridConfig.y - y) * scale + 4;
      ctx.fillText(y.toString(), offsetX - 10, canvasY);
    }
  }, [ctx, gridConfig, offsetX, offsetY, scale]);

  // renderBasePolygon removed as per redesign requirements
  const renderAssets = useCallback(() => {
    if (!ctx || !assets) return;

    // Render Metal detectors (1x1 meter grey squares)
    assets.metaldetectors?.forEach((md: AssetCoordinate, index: number) => {
      const pos = gridToCanvas(md.x, md.y);
      const size = 1 * scale; // 1x1 meter

      ctx.fillStyle = '#6b7280'; // Grey color
      ctx.fillRect(pos.x - size/2, pos.y - size/2, size, size);
      
      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 1;
      ctx.strokeRect(pos.x - size/2, pos.y - size/2, size, size);
    });

    // Render Body scanners (1x0.5 meter grey rectangles)
    assets.bodyscanner?.forEach((bs: AssetCoordinate, index: number) => {
      const pos = gridToCanvas(bs.x, bs.y);
      const width = 1 * scale; // 1 meter width for passenger entry
      const height = 0.5 * scale; // 0.5 meter height

      ctx.fillStyle = '#6b7280'; // Grey color
      ctx.fillRect(pos.x - width/2, pos.y - height/2, width, height);
      
      ctx.strokeStyle = '#4b5563';
      ctx.lineWidth = 1;
      ctx.strokeRect(pos.x - width/2, pos.y - height/2, width, height);
    });

    // Render Conveyor belts (ladder-like structure, 1 meter width)
    assets.conveyorbelt?.forEach((belt: ConveyorBelt, index: number) => {
      const startPos = gridToCanvas(belt.start.x, belt.start.y);
      const endPos = gridToCanvas(belt.end.x, belt.end.y);
      const beltWidth = 1 * scale; // 1 meter width
      
      // Calculate perpendicular vector for belt width
      const dx = endPos.x - startPos.x;
      const dy = endPos.y - startPos.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      const perpX = (-dy / length) * (beltWidth / 2);
      const perpY = (dx / length) * (beltWidth / 2);

      // Draw outer boundary lines (ladder sides)
      ctx.strokeStyle = '#6b7280'; // Grey color
      ctx.lineWidth = 2;
      ctx.beginPath();
      // Top edge
      ctx.moveTo(startPos.x + perpX, startPos.y + perpY);
      ctx.lineTo(endPos.x + perpX, endPos.y + perpY);
      // Bottom edge
      ctx.moveTo(startPos.x - perpX, startPos.y - perpY);
      ctx.lineTo(endPos.x - perpX, endPos.y - perpY);
      ctx.stroke();

      // Draw cross segments (ladder rungs) every ~0.5 meters
      const segmentLength = 0.5 * scale;
      const numSegments = Math.floor(length / segmentLength);
      
      ctx.lineWidth = 1;
      for (let i = 1; i < numSegments; i++) {
        const t = i / numSegments;
        const segX = startPos.x + dx * t;
        const segY = startPos.y + dy * t;
        
        ctx.beginPath();
        ctx.moveTo(segX + perpX, segY + perpY);
        ctx.lineTo(segX - perpX, segY - perpY);
        ctx.stroke();
      }
    });
  }, [ctx, assets, gridToCanvas, scale]);

  // ✅ ADD: New function to render polygon boundaries on hover
const renderPolygonBoundaries = useCallback(() => {
  if (!ctx || !checkpointConfig?.heatmap || !hoveredZone) return;

  const heatmapData = checkpointConfig.heatmap[hoveredZone];
  if (!heatmapData?.polygon || heatmapData.polygon.length === 0) return;

  // Draw highlighted polygon boundary
  ctx.save();
  ctx.strokeStyle = '#60a5fa'; // Bright blue border
  ctx.lineWidth = 3; // Thick border for visibility
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.setLineDash([5, 5]); // Dashed line for emphasis

  ctx.beginPath();
  
  heatmapData.polygon.forEach((point, index) => {
    const canvasPos = gridToCanvas(point.x, point.y);
    if (index === 0) {
      ctx.moveTo(canvasPos.x, canvasPos.y);
    } else {
      ctx.lineTo(canvasPos.x, canvasPos.y);
    }
  });
  
  ctx.closePath();
  ctx.stroke();
  ctx.restore();
}, [ctx, checkpointConfig, hoveredZone, gridToCanvas]);

  const renderHeatmap = useCallback(() => {
    if (!ctx || !checkpointConfig?.heatmap || !occupancyData) return;

    Object.entries(checkpointConfig.heatmap).forEach(([assetCode, heatmapData]) => {
      const occupancy = occupancyData[assetCode] || 0; // Default to 0 if no data
      const activeThresholds = thresholds?.[assetCode] || defaultThresholds;
      const fillColor = getColorFromOccupancy(occupancy, activeThresholds);
      
      // Use polygon coordinates if available, fallback to old coordinates structure
      const polygonCoords = heatmapData.polygon;
      if (polygonCoords && polygonCoords.length > 0) {
        // Draw filled polygon without borders
        ctx.fillStyle = fillColor;
        ctx.beginPath();
        
        polygonCoords.forEach((point, index) => {
          const canvasPos = gridToCanvas(point.x, point.y);
          if (index === 0) {
            ctx.moveTo(canvasPos.x, canvasPos.y);
          } else {
            ctx.lineTo(canvasPos.x, canvasPos.y);
          }
        });
        
        ctx.closePath();
        ctx.fill();
        // No stroke/border as per requirements
        
        // NEW: Render tag labels on canvas
        if (heatmapData.tagLabel && heatmapData.tagLabel.length > 0) {
          heatmapData.tagLabel.forEach(label => {
            renderTagLabel(ctx, label, scale, gridToCanvas);
          });
        }
      }
    });
  }, [ctx, checkpointConfig, occupancyData, thresholds, defaultThresholds, getColorFromOccupancy, gridToCanvas, renderTagLabel]);

  // Render passenger flow with smooth curves and color wave animation
  const renderPassengerCylinders = useCallback(() => {
    if (!ctx || !animationData.size) return;

    animationData.forEach((animData, assetCode) => {
      const { pathCoordinates, dotPositions, wavePosition } = animData;
      
      // Get base color based on flow rate thresholds
      const flowOut = flowData?.[assetCode] || 0;
      const activeThresholds = thresholds?.[assetCode] || defaultThresholds;
      const minFlow = (activeThresholds as any).minflow || 5;
      const maxFlow = (activeThresholds as any).maxflow || 20;
      
      // Calculate flow boundaries (same as in passengerFlowAnimationService)
      const avgLowerBound = 1.3 * minFlow;
      const avgUpperBound = 0.75 * maxFlow;
      
      // Determine base dot color based on flow rate
      let baseColor = '#22c55e'; // green (default for fast flow)
      if (flowOut < avgLowerBound) {
        baseColor = '#ef4444'; // red for slow flow (congested)
      } else if (flowOut <= avgUpperBound) {
        baseColor = '#eab308'; // yellow for average flow
      }
      // else remains green for fast flow
      
      // Only show the path line if this zone is being hovered
      const showPath = hoveredZone === assetCode;
      
      // Draw smooth curved path when hovering
      if (showPath && pathCoordinates && pathCoordinates.length > 1) {
        ctx.save();
        
        // Draw the smooth freehand-style path
        ctx.strokeStyle = '#60a5fa'; // Bright blue for highlighted path
        ctx.lineWidth = 2.5;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.setLineDash([0]); // Solid line
        
        ctx.beginPath();
        
        if (pathCoordinates.length > 2) {
          // Move to first point
          const firstPos = gridToCanvas(pathCoordinates[0].x, pathCoordinates[0].y);
          ctx.moveTo(firstPos.x, firstPos.y);
          
          // Create smooth curve through all points
          for (let i = 1; i < pathCoordinates.length - 1; i++) {
            const curr = gridToCanvas(pathCoordinates[i].x, pathCoordinates[i].y);
            const next = gridToCanvas(pathCoordinates[i + 1].x, pathCoordinates[i + 1].y);
            
            // Use quadratic curve for smooth path
            const cpX = curr.x;
            const cpY = curr.y;
            const endX = (curr.x + next.x) / 2;
            const endY = (curr.y + next.y) / 2;
            
            ctx.quadraticCurveTo(cpX, cpY, endX, endY);
          }
          
          // Draw to last point
          const lastPos = gridToCanvas(
            pathCoordinates[pathCoordinates.length - 1].x,
            pathCoordinates[pathCoordinates.length - 1].y
          );
          const secondLastPos = gridToCanvas(
            pathCoordinates[pathCoordinates.length - 2].x,
            pathCoordinates[pathCoordinates.length - 2].y
          );
          ctx.quadraticCurveTo(secondLastPos.x, secondLastPos.y, lastPos.x, lastPos.y);
        } else {
          // For paths with only 2 points, draw straight line
          const start = gridToCanvas(pathCoordinates[0].x, pathCoordinates[0].y);
          const end = gridToCanvas(pathCoordinates[1].x, pathCoordinates[1].y);
          ctx.moveTo(start.x, start.y);
          ctx.lineTo(end.x, end.y);
        }
        
        ctx.stroke();
        
        // Draw arrow at the exit (last coordinate)
        const lastCoord = pathCoordinates[pathCoordinates.length - 1];
        const secondLastCoord = pathCoordinates[pathCoordinates.length - 2] || pathCoordinates[pathCoordinates.length - 1];
        
        const lastPos = gridToCanvas(lastCoord.x, lastCoord.y);
        const secondLastPos = gridToCanvas(secondLastCoord.x, secondLastCoord.y);
        
        // Calculate arrow direction
        const dx = lastPos.x - secondLastPos.x;
        const dy = lastPos.y - secondLastPos.y;
        const angle = Math.atan2(dy, dx);
        
        // Draw arrow head at exit
        ctx.fillStyle = '#60a5fa'; // Same blue color
        ctx.strokeStyle = '#60a5fa';
        ctx.lineWidth = 2.5;
        
        const arrowLength = 12;
        const arrowAngle = Math.PI / 6; // 30 degrees
        
        ctx.beginPath();
        // Draw filled arrow head
        ctx.moveTo(lastPos.x, lastPos.y);
        ctx.lineTo(
          lastPos.x - arrowLength * Math.cos(angle - arrowAngle),
          lastPos.y - arrowLength * Math.sin(angle - arrowAngle)
        );
        ctx.lineTo(
          lastPos.x - arrowLength * 0.7 * Math.cos(angle),
          lastPos.y - arrowLength * 0.7 * Math.sin(angle)
        );
        ctx.lineTo(
          lastPos.x - arrowLength * Math.cos(angle + arrowAngle),
          lastPos.y - arrowLength * Math.sin(angle + arrowAngle)
        );
        ctx.closePath();
        ctx.fill();
        
        // Add "EXIT" label near the arrow
        ctx.font = EXIT_FONT;
        ctx.fillStyle = '#60a5fa';
        ctx.textAlign = 'center';
        ctx.fillText('EXIT', lastPos.x + 20 * Math.cos(angle), lastPos.y + 20 * Math.sin(angle) - 5);
        
        ctx.restore();
      }

      // Calculate dot radius (doubled from 0.15 to 0.3)
      const dotRadius = 0.3 * scale; // 2x larger dots
      
      // Draw fixed dots with color wave animation
      if (dotPositions && dotPositions.length > 0) {
        dotPositions.forEach((position, dotIndex) => {
          // Get the actual coordinate along the path
          const coord = passengerFlowAnimationService.getPositionAlongPath(pathCoordinates, position);
          const pos = gridToCanvas(coord.x, coord.y);
          
          // Calculate color intensity based on wave position
          const intensity = passengerFlowAnimationService.getDotIntensity(position, wavePosition);
          
          // Create color based on intensity
          let dotColor;
          if (intensity > 0) {
            // Mix base color with much darker shade based on intensity
            // Parse base color to RGB
            const r = parseInt(baseColor.slice(1, 3), 16);
            const g = parseInt(baseColor.slice(3, 5), 16);
            const b = parseInt(baseColor.slice(5, 7), 16);
            
            // Darken based on intensity (wave effect) - now 60% darker for more visibility
            const factor = 1 - (intensity * 0.6); // Increased from 0.4 to 0.6 for two shades darker
            const newR = Math.floor(r * factor);
            const newG = Math.floor(g * factor);
            const newB = Math.floor(b * factor);
            
            dotColor = `rgb(${newR}, ${newG}, ${newB})`;
          } else {
            dotColor = baseColor;
          }
          
          // Draw dot with solid color (no gradient)
          ctx.fillStyle = dotColor;
          ctx.strokeStyle = '#1f2937';
          ctx.lineWidth = 1;
          
          // Draw the dot
          ctx.beginPath();
          ctx.arc(pos.x, pos.y, dotRadius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();
        });
      }
    });
  }, [ctx, animationData, flowData, thresholds, defaultThresholds, gridToCanvas, scale, hoveredZone]);

  // Main rendering function with layer sequence: Grid → Heatmap Polygons → Assets → Passenger Cylinders  
  // const renderAllLayers = useCallback(() => {
  //   if (!ctx || !canvasRef.current || canvasSize.width === 0 || canvasSize.height === 0) return;

  //   const canvas = canvasRef.current;
    
  //   // Clear canvas
  //   ctx.fillStyle = '#111827';
  //   ctx.fillRect(0, 0, canvas.width, canvas.height);

  //   // Layer 1: Base Grid
  //   if (sequenceConfig.layerSequence.layers['1']?.enabled) {
  //     renderBaseGrid();
  //   }

  //   // Layer 2: Heatmap Polygons (render before assets for proper layering)
  //   if (occupancyData) {
  //     renderHeatmap();
  //   }

  //   // Layer 3: Assets (metal detectors, body scanners, conveyor belts)
  //   if (sequenceConfig.layerSequence.layers['3']?.enabled) {
  //     renderAssets();
  //   }

  //   // Layer 4: Passenger flow cylinders
  //   renderPassengerCylinders();
  // }, [ctx, canvasSize, sequenceConfig, renderBaseGrid, renderAssets, renderHeatmap, renderPassengerCylinders, occupancyData]);

  // Updated main rendering function with polygon boundaries on hover
  // ✅ MODIFY: Update renderAllLayers function
const renderAllLayers = useCallback(() => {
  if (!ctx || !canvasRef.current || canvasSize.width === 0 || canvasSize.height === 0) return;

  const canvas = canvasRef.current;
  
  // Clear canvas
  ctx.fillStyle = '#111827';
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Layer 1: Base Grid
  if (sequenceConfig.layerSequence.layers['1']?.enabled) {
    renderBaseGrid();
  }

  // Layer 2: Heatmap Polygons (render before assets for proper layering)
  if (occupancyData) {
    renderHeatmap();
  }

  // Layer 3: Assets (metal detectors, body scanners, conveyor belts)
  if (sequenceConfig.layerSequence.layers['3']?.enabled) {
    renderAssets();
  }

  // Layer 4: Passenger flow cylinders
  renderPassengerCylinders();

  // ✅ ADD: Layer 5: Polygon boundaries on hover (render last so it's on top)
  renderPolygonBoundaries();
  
}, [ctx, canvasSize, sequenceConfig, renderBaseGrid, renderAssets, renderHeatmap, renderPassengerCylinders, renderPolygonBoundaries, occupancyData]);

  // Render loop
  useEffect(() => {
    if (ctx && canvasSize.width > 0 && canvasSize.height > 0) {
      renderAllLayers();
    }
  }, [ctx, canvasSize, renderAllLayers]);

  // Render when data changes (no animation needed)
  useEffect(() => {
    if (ctx && canvasSize.width > 0 && canvasSize.height > 0) {
      renderAllLayers();
    }
  }, [ctx, canvasSize, renderAllLayers, occupancyData]);

  // Initialize flow animation when flow data changes
  useEffect(() => {
    if (flowData && checkpointConfig.heatmap) {
      // Initialize flow for each asset with flow data
      Object.entries(flowData).forEach(([assetCode, flowOut]) => {
        const heatmapData = checkpointConfig.heatmap?.[assetCode];
        if (heatmapData && heatmapData.passengerPath && flowOut > 0) {
          // Get minFlow and maxFlow from API thresholds first, fallback to config, then defaults
          let minFlow = 5;  // Default value
          let maxFlow = 20; // Default value
          
          // Use API threshold values if available, otherwise use config values
          const apiThreshold = thresholds?.[assetCode];
          if (apiThreshold) {
            minFlow = apiThreshold.minflow;
            maxFlow = apiThreshold.maxflow;
          } else if ((heatmapData as any).minflow !== undefined && (heatmapData as any).maxflow !== undefined) {
            // Fallback to config values if no API thresholds
            minFlow = (heatmapData as any).minflow;
            maxFlow = (heatmapData as any).maxflow;
          }
          
          passengerFlowAnimationService.initializeFlow(
            assetCode,
            flowOut,
            heatmapData.passengerPath,
            minFlow,
            maxFlow
          );
        }
      });
      
      // Start animation with faster update interval for smoother movement
      passengerFlowAnimationService.startAnimation(50);
    }
    
    // Subscribe to animation updates
    const unsubscribe = passengerFlowAnimationService.subscribe((data) => {
      setAnimationData(new Map(data));
    });
    
    return () => {
      unsubscribe();
      passengerFlowAnimationService.stopAnimation();
      passengerFlowAnimationService.clearAll();
    };
  }, [JSON.stringify(flowData), checkpointConfig.metadata?.checkpointId, JSON.stringify(thresholds)]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      <canvas
        ref={canvasRef}
        className="w-full h-full cursor-crosshair"
        style={{ background: '#111827' }}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      />
      
      {/* Hover Tooltip */}
      {hoveredZone && occupancyData && (
        <div 
          className="absolute z-50 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl border border-gray-700 pointer-events-none"
          style={{
            left: `${mousePosition.x}px`,
            top: `${mousePosition.y}px`,
            transform: 'translate(-50%, -110%)',
            minWidth: '200px' // ✅ ADD: Ensure enough width for tags
          }}
        >
          {/* ✅ ADD: Zone ID */}
    {/* <div className="text-xs text-gray-400 mb-1 font-mono">
      {hoveredZone}
    </div> */}
    {/* ✅ ADD: Tags display */}
    {checkpointConfig.heatmap?.[hoveredZone]?.tag && checkpointConfig.heatmap[hoveredZone].tag!.length > 0 && (
      <div className="mb-2">
        <div className="text-xs text-gray-400 mb-1">Passenger Types:</div>
        <div className="flex flex-wrap gap-1">
          {checkpointConfig.heatmap[hoveredZone].tag!.map((tag, index) => (
            <span 
              key={index}
              className="px-2 py-1 bg-blue-600 text-white text-xs rounded-full font-medium"
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
    )}
          <div className="text-xs flex items-center space-x-1">
            <span className="text-gray-400">Occupancy:</span>
            <span className="font-bold text-white">{occupancyData[hoveredZone] || 0}</span>
            <span className="text-gray-400">people</span>
          </div>
          {flowData && (
            <div className="text-xs flex items-center space-x-1">
              <span className="text-gray-400">Outflow:</span>
              <span className="font-bold text-white">{flowData[hoveredZone] || 0}</span>
              <span className="text-gray-400">people/min</span>
            </div>
          )}
          {/* Small arrow pointing down to the zone */}
          <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 border-r border-b border-gray-700 rotate-45"></div>
        </div>
      )}
      
      {(!occupancyData || canvasSize.width === 0) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-gray-400">
            {canvasSize.width === 0 ? 'Initializing canvas...' : 'Loading occupancy data...'}
          </div>
        </div>
      )}
    </div>
  );
};